/**
 * Copyright 2025 Google LLC. This software is provided as-is, without warranty 
 * or representation for any use or purpose. Your use of it is subject to your 
 * agreement with Google.
 */
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { OKTA_AUTH } from '@okta/okta-angular';
import { BehaviorSubject, catchError, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class GuidelineService {

  session_id: string = '';
  private oktaAuth = inject(OKTA_AUTH);
  private disableFuqs = new BehaviorSubject<boolean>(false);
  disableFuqPanels$ = this.disableFuqs.asObservable();
  constructor(public http: HttpClient) { }
  
  setSessionId(session_id: any) {
    this.session_id = session_id;
  }
  getSessionId(): string {
    return this.session_id;
  }
  setDisableFuqPanels(value: boolean) {
    this.disableFuqs.next(value);
  }
  generateBotResponse(userQuestion: string, context: any, session_id: any) {
    const body =
    {
      "question": userQuestion,
      "member_context_full": {
        "member_id": "angular",
        "session_id": session_id
      },
    }
    let endpoint = '/api/respond'
    const headers = this.getHeadersFromOktaAuth();
    return this.http.post(endpoint, body, { headers: headers }).pipe(
      catchError(this.handleError)
    );
  }

  generateFUQResponse(userQuestion: string, context: any, session_id: any) {
    const body =
    {
      "question": userQuestion,
      "member_context_full": {
        "member_id": "angular",
        "session_id": session_id
      },
    }
    let endpoint = '/api/follow-up-questions'
    const headers = this.getHeadersFromOktaAuth();
    return this.http.post(endpoint, body, { headers: headers }).pipe(
      catchError(this.handleError)
    );
  }
  generateFeedbackResponse(session_id: string, qid: string, feedback: number) {
    const body =
    {
      "qid": qid,
      "session_id": session_id,
      "feedback": feedback
    }
    let endpoint = '/api/feedback'
    const headers = this.getHeadersFromOktaAuth();
    return this.http.post(endpoint, body, { headers: headers }).pipe(
      catchError(this.handleError)
    );
  }

  generateFeedbackComment(session_id: string, qid: string, comment: string, feedback: number) {
    const body =
    {
      "qid": qid,
      "session_id": session_id,
      "feedback": feedback,
      "feedback_comment": comment,
    }

    let endpoint = '/api/feedback-comment'
    const headers = this.getHeadersFromOktaAuth();
    return this.http.post(endpoint, body, { headers: headers }).pipe(
      catchError(this.handleError)
    );
  }

  generatePreviewContent(ch_id: string[], cit_content: string, relevance_score: string, uri: string, vais_content: string[]) {
    const body =
    {
      "ch_id": ch_id,
      "cit_content": cit_content,
      "relevance_score": relevance_score.toString(),
      "uri": uri,
      "vais_content": vais_content
    }
    let endpoint = '/api/preview-content'
    const headers = this.getHeadersFromOktaAuth();
    return this.http.post(endpoint, body, { headers: headers }).pipe(
      catchError(this.handleError)
    );
  }

  batchPreviewContent(citations: any) {
    const body = {
      "requests": citations
    }
    let endpoint = '/api/batch-preview-content'
    const headers = this.getHeadersFromOktaAuth();
    return this.http.post(endpoint, body, { headers: headers }).pipe(
      catchError(this.handleError)
    );
  }

  getStarterQuestions() {
    let endpoint = '/api/starter-questions'
    const headers = this.getHeadersFromOktaAuth();
    return this.http.get<any>(endpoint, { headers: headers }).pipe(
      catchError(this.handleError)
    );
  }

  getHeadersFromOktaAuth() {
    const accessToken = this.oktaAuth.getAccessToken();
    const headers = {
      "Authorization": `${accessToken}`
    }
    return headers;
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'An unknown error occurred!';
    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Client-side error: ${error.error.message}`;
    } else {
      // Server-side error
      errorMessage = `Server error: ${error.status} - ${error.message}`;
    }
    return throwError(() => new Error(errorMessage));
  }

}

